import { useCallback, useEffect, useState } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import type { ISelectOption } from '@creditflowresearch/uilib/Select';
import { getContainer } from '@framework/di/getContainer';
import Logger from '@framework/sentry/Logger';
import { MIN_QUERY_LENGTH } from '@/creditflowresearch/application/BondScreener/constants';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';
import BondScreenersRepository
  from '@/creditflowresearch/infrastructure/repository/BondScreenersRepository';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import type {
  TFilterPayload,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilter';
import {
  createUseSearchSelectFilter,
} from '@/creditflowresearch/ui/common/hooks/sidebar/createUseSearchSelectFilter';
import useExcludeToggle from '@/creditflowresearch/ui/common/hooks/sidebar/useExcludeToggle';
import useCheckboxFilter from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/hooks/useCheckboxFilter';
import type { ICheckboxOption } from '@/creditflowresearch/ui/common/hooks/sidebar/useCheckboxFilter';
import type { ILeadCompany } from '@/creditflowresearch/domain/vo/Bondscreener';
import { buildLeadRoleOptions } from './constants';

const useSearchSelect = createUseSearchSelectFilter<TFilterPayload>(useCorporateBondScreenerSidebarFilterContext);

const toOptions = (companies: ILeadCompany[]): ISelectOption[] =>
  companies.map(({ id, name }) => ({ value: id, label: name }));

const useLeadsSection = () => {
  const repository = getContainer().get<BondScreenersRepository>(BondScreenersRepository);
  const dictionaryRepository = getContainer().get<DictionaryRepository>(DictionaryRepository);
  const { localFilters } = useCorporateBondScreenerSidebarFilterContext();

  const [roleOptions, setRoleOptions] = useState<ICheckboxOption<number>[]>([]);
  const [currentOptions, setCurrentOptions] = useState<ISelectOption[]>([]);

  const { isExcluded, activeKey, onToggleExclude } = useExcludeToggle({
    includeKey: 'includedLeadIds',
    excludeKey: 'excludedLeadIds',
    stateKey: 'isLeadsExcluded',
    useFilterContext: useCorporateBondScreenerSidebarFilterContext,
  });

  const leads = useSearchSelect({ filterKey: activeKey });

  const roles = useCheckboxFilter({ filterKey: 'includedLeadRoleIds', items: roleOptions });

  const hasLeadsSelected = leads.selectedOptions.length > 0;

  useEffect(() => {
    const loadRoles = async (): Promise<void> => {
      try {
        const dictionary = await dictionaryRepository.getCompanyRoles();
        setRoleOptions(buildLeadRoleOptions(dictionary));
      } catch (error) {
        Logger.error({ message: 'Failed to load lead roles', error });
      }
    };
    loadRoles();
  }, [dictionaryRepository]);

  useEffect(() => {
    const leadIds = localFilters[activeKey] ?? [];
    if (leadIds.length === 0) return;

    const resolveInitialOptions = async (): Promise<void> => {
      try {
        const companies = await repository.searchLeads({ includedCompanyIds: leadIds });
        leads.onChange(toOptions(companies.filter(({ id }) => leadIds.includes(id))));
      } catch (error) {
        Logger.error({ message: 'Failed to resolve initial lead names', error });
      }
    };
    resolveInitialOptions();
  }, []);

  const searchLeads = useCallback(async (
    query: string,
    callback: (options: ISelectOption[]) => void,
  ) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < MIN_QUERY_LENGTH.lead) {
      callback([]);
      return [];
    }
    setCurrentOptions([]);
    try {
      const companies = await repository.searchLeads({ search: trimmedQuery });
      const options = toOptions(companies);
      setCurrentOptions(options);
      callback(options);
      return options;
    } catch (error) {
      Logger.error({ message: 'Failed to search leads', error });
      AlertManager.error('Failed to load leads');
      callback([]);
      return [];
    }
  }, [repository]);

  const handleChange = useCallback((selected: ISelectOption[]): void => {
    leads.onChange(selected);
    if (selected.length === 0) {
      roles.clear();
    }
  }, [leads.onChange, roles.clear]);

  return {
    searchLeads,
    selectedOptions: leads.selectedOptions,
    handleChange,
    currentOptions,
    roles,
    hasLeadsSelected,
    isExcluded,
    onToggleExclude,
  };
};

export default useLeadsSection;
